import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { catchError, mapTo, Observable, of, switchMap, take, tap } from 'rxjs';
import { UserConfiguration } from '../../interfaces/userConfiguration.interface';

@Injectable({
  providedIn: 'root',
})
export class UserConfigurationService {
  private userConfigurationEndpoint = '/me/configuration';

  /** Persisted when the start-page user guide tour was completed or dismissed. */
  public readonly KEY_USER_GUIDE_SEEN = 'USER_GUIDE_SEEN';

  /** Persisted when the isolated "For You" guide step was completed or dismissed. */
  public readonly KEY_FOR_YOU_GUIDE_SEEN = 'FOR_YOU_GUIDE_SEEN';

  // If set, the feedback popup should be shown
  public readonly KEY_SHOW_FEEDBACK_POPUP = "SHOW_FEEDBACK_POPUP";

  // global configuration for allowed custom app urls
  public readonly WHITELIST_APP_URL = "WHITELIST_APP_URL";

  // global configuration listing the allowed user-configuration keys (comma separated string)
  public readonly KEY_USER_CONFIG_KEYS = "USER_CONFIG_KEYS";

  private userConfiguration: UserConfiguration | undefined;

  constructor(private http: HttpClient) {}

  public fetchUserConfiguration(): Observable<void> {
    return this.http
      .get<UserConfiguration>(environment.baseUrl + this.userConfigurationEndpoint)
      .pipe(
        take(1),
        tap((cfg) => (this.userConfiguration = cfg)),
        mapTo(void 0),
        catchError(() => of(void 0)),
      );
  }

  public getOrDefault(key: string, defaultValue?: any): any | undefined {
    const raw = this.userConfiguration?.[key];
    if (raw === undefined || raw === null || raw === '') return defaultValue ?? undefined;

    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }

  public set(key: string, value: any): Observable<void> {
    const valueBody = JSON.stringify(value);
    return this.http
      .put<UserConfiguration>(
        environment.baseUrl + this.userConfigurationEndpoint + '/' + key,
        valueBody,
      )
      .pipe(
        take(1),
        switchMap(() => this.fetchUserConfiguration()),
      );
  }
}
